import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {ChartModule} from 'angular-highcharts';
import {I18NextModule, I18NextTitle} from 'angular-i18next';
import {AppTitleComponent} from 'app/shared/components/app-title/app-title.component';
import {CopyToClipboardComponent} from 'app/shared/components/copy-to-clipboard/copy-to-clipboard.component';
import {DateRangeInputComponent} from 'app/shared/components/date-range-input/date-range-input.component';
import {InlineSelectorItemComponent} from 'app/shared/components/inline-selector-item/inline-selector-item.component';
import {InlineSelectorComponent} from 'app/shared/components/inline-selector/inline-selector.component';
import {PageTitleComponent} from 'app/shared/components/page-title/page-title.component';
import {PointDetailsComponent} from 'app/shared/components/point-details/point-details.component';
import {I18NextTitleService} from '../core/services/i18-next-title.service';
import {AccordionComponent} from './components/accordion/accordion.component';
import {AnimatedContentComponent} from './components/animated-content/animated-content.component';
import {ArrayInputComponent} from './components/array-input/array-input.component';
import {CalendarComponent} from './components/calendar/calendar.component';
import {CheckboxTreeComponent} from './components/checkbox-tree/checkbox-tree.component';
import {CheckboxComponent} from './components/checkbox/checkbox.component';
import {CountryPickerComponent} from './components/country-picker/country-picker.component';
import {DateEditComponent} from './components/date-edit/date-edit.component';
import {DateInputComponent} from './components/date-input/date-input.component';
import {DropDownListComponent} from './components/drop-down-list/drop-down-list.component';
import {ListOptionComponent} from './components/drop-down-list/list-option/list-option.component';
import {DropDownMenuComponent} from './components/drop-down-menu/drop-down-menu.component';
import {MenuButton} from './components/drop-down-menu/menu-button/menu-button.component';
import {MenuItem} from './components/drop-down-menu/menu-item/menu-item.component';
import {DropDownPickerComponent} from './components/drop-down-picker/drop-down-picker.component';
import {DropDownContentComponent} from './components/drop-down/drop-down-content/drop-down-content.component';
import {DropDownModernContentComponent} from './components/drop-down/drop-down-modern-content/drop-down-modern-content.component';
import {DropDownComponent} from './components/drop-down/drop-down.component';
import {EmailInputComponent} from './components/email-input/email-input.component';
import {EnumOutputComponent} from './components/enum-output/enum-output.component';
import {FileInputComponent} from './components/file-input/file-input.component';
import {FormErrorsComponent} from './components/form-errors/form-errors.component';
import {CellsComponent} from './components/grid/components/cells/cells.component';
import {ColumnsComponent} from './components/grid/components/columns/columns.component';
import {GridHeaderCellComponent} from './components/grid/components/grid-header-cell/grid-header-cell.component';
import {Cell} from './components/grid/directives/cell/cell.directive';
import {Column} from './components/grid/directives/column/column.directive';
import {GridComponent} from './components/grid/grid.component';
import {HeadingSeparatorComponent} from './components/heading-separator/heading-separator.component';
import {HintIconComponent} from './components/hint-icon/hint-icon.component';
import {ImageHintIconComponent} from './components/image-hint-icon/image-hint-icon.component';
import {InputErrorsTooltipComponent} from './components/input-errors-tooltip/input-errors-tooltip.component';
import {InputErrorsComponent} from './components/input-errors/input-errors.component';
import {KnowledgeBaseLinkComponent} from './components/knowledge-base-link/knowledge-base-link.component';
import {NumberInputComponent} from './components/number-input/number-input.component';
import {OverlayOutletComponent} from './components/overlay-outlet/overlay-outlet.component';
import {PageControlsComponent} from './components/page-controls/page-controls.component';
import {PageTabComponent} from './components/page-tab/page-tab.component';
import {PageTabsComponent} from './components/page-tabs/page-tabs.component';
import {PageTitleContentComponent} from './components/page-title-content/page-title-content.component';
import {PageTitleItemComponent} from './components/page-title-item/page-title-item.component';
import {PasswordInputComponent} from './components/password-input/password-input.component';
import {PercentInputComponent} from './components/percent-input/percent-input.component';
import {PriceInputComponent} from './components/price-input/price-input.component';
import {RadioComponent} from './components/radio/radio.component';
import {RouterLoadingComponent} from './components/router-loading/router-loading.component';
import {RouterOutletLoadingComponent} from './components/router-outlet-loading/router-outlet-loading.component';
import {SearchInputComponent} from './components/search-input/search-input.component';
import {ServerTimeComponent} from './components/server-time/server-time.component';
import {SideBarContentComponent} from './components/side-bar-content/side-bar-content.component';
import {CellTemplateDirective} from './components/simple-grid/cell-template.directive';
import {ColumnTemplateDirective} from './components/simple-grid/column-template.directive';
import {RowTooltipDirective} from './components/simple-grid/row-tooltip.directive';
import {SimpleGridComponent} from './components/simple-grid/simple-grid.component';
import {SvgImageComponent} from './components/svg-image/svg-image.component';
import {SwitchComponent} from './components/switch/switch.component';
import {TabComponent} from './components/tabs/tab/tab.component';
import {TabsComponent} from './components/tabs/tabs.component';
import {TimeInputComponent} from './components/time-input/time-input.component';
import {TooltipOutletComponent} from './components/tooltip-outlet/tooltip-outlet.component';
import {TumblerComponent} from './components/tumbler/tumbler.component';
import {WarningComponent} from './components/warning/warning.component';
import {AutoFocusDirective} from './directives/auto-focus.directive';
import {ContentTemplateDirective} from './directives/content-template.directive';
import {CustomControlDirective} from './directives/custom-control.directive';
import {CustomPropertyDirective} from './directives/custom-property.directive';
import {DropDownDirective} from './directives/drop-down.directive';
import {LabelTemplateDirective} from './directives/label-template.directive';
import {RouterLinkDirective} from './directives/router-link.directive';
import {ServerValidationOnBlurDirective} from './directives/server-validation-on-blur.directive';
import {SubmitStatusDirective} from './directives/submit-status.directive';
import {TooltipDirective} from './directives/tooltip.directive';
import {AppUrlPipe} from './pipes/app-url.pipe';
import {NumberFormatPipe} from './pipes/number-format.pipe';
import {ShortNumberFormatPipe} from './pipes/short-number-format.pipe';
import {USDPipe} from './pipes/usd.pipe';
import {ResizeDirective} from './directives/resize.directive';
import {SdkStatusComponent} from 'app/components/app-statuses/sdk-status/sdk-status.component';
import {IntegrationStatusComponent} from 'app/components/app-statuses/integration-status/integration-status.component';
import {HubspotTrackingFormDirective} from 'app/shared/directives/hubspot-tracking-form.directive';
import {DurationFormatPipe} from 'app/shared/pipes/duration-format.pipe';
import {ElapsedTextTooltipDirective} from './directives/elapsed-text-tooltip.directive';
import {FormatMetricValuePipe} from './pipes/format-metric-value.pipe';
import {ModalComponent} from './components/modal/modal.component';
import {InlineSelectComponent} from './components/inline-select/inline-select.component';
import {SkadnetworkStatusComponent} from 'app/components/app-statuses/skadnetwork-status/skadnetwork-status.component';
import {AppStatusesComponent} from 'app/components/app-statuses/app-statuses.component';
import {HeaderContentComponent} from './components/header-content/header-content.component';
import {CopyAlertComponent} from './components/copy-alert/copy-alert.component';
import {DropDownColorsListComponent} from './components/drop-down-colors-list/drop-down-colors-list.component';
import {FeedbackPopupComponent} from './components/feedback-popup/feedback-popup.component';
import {EmptyStateComponent} from 'app/shared/components/empty-state/empty-state.component';
import {SafePipe} from './pipes/safe.pipe';
import {TrackFocusDirective} from './directives/track-focus.directive';
import {SimpleMultiSelectComponent} from 'app/features/analytics/shared/components/simple-multi-select/simple-multi-select.component';
import {AppManageSelectComponent} from './components/app-manage-select/app-manage-select.component';
import {PaginationComponent} from './components/pagination/pagination.component';
import {FormGroupComponent} from './components/form/form-group/form-group.component';
import {FormGroupItemComponent} from './components/form/form-group-item/form-group-item.component';
import {NzPopoverModule} from 'ng-zorro-antd/popover';
import {DynamicModule} from 'ng-dynamic-component';
import {AgGridSearchComponent} from './components/ag-grid-search/ag-grid-search.component';
import {SavedReportListComponent} from 'app/features/analytics/features/reports/components/reports-filters/saved-report-list/saved-report-list.component';
import {StatsHelperService} from 'app/features/analytics/service/stats-helper.service';
import {SavedReportFormComponent} from 'app/features/analytics/features/reports/components/reports-filters/saved-report-form/saved-report-form.component';
import {HeaderReportFilters} from 'app/features/analytics/features/reports/components/reports-filters/header-report-filters/header-report-filters.component';
import {RenameReportFormComponent} from 'app/features/analytics/features/reports/components/reports-filters/rename-report-form/rename-report-form.component';
import {BreadcrumbsComponent} from 'app/features/apps/components/app-breadcrumbs/app-breadcrumbs.component';

@NgModule({
    imports: [CommonModule, I18NextModule, FormsModule, ReactiveFormsModule, RouterModule, ChartModule, DynamicModule, NzPopoverModule],
    declarations: [
        DropDownListComponent,
        DropDownMenuComponent,
        CheckboxComponent,
        TumblerComponent,
        RadioComponent,
        DateEditComponent,
        DateInputComponent,
        TimeInputComponent,
        NumberInputComponent,
        EmailInputComponent,
        PasswordInputComponent,
        InputErrorsComponent,
        InputErrorsTooltipComponent,
        TooltipOutletComponent,
        AccordionComponent,
        DropDownPickerComponent,
        CalendarComponent,
        TooltipDirective,
        ElapsedTextTooltipDirective,
        AutoFocusDirective,
        CustomPropertyDirective,
        NumberFormatPipe,
        DurationFormatPipe,
        FormatMetricValuePipe,
        ShortNumberFormatPipe,
        AppUrlPipe,
        ServerValidationOnBlurDirective,
        DropDownComponent,
        CheckboxTreeComponent,
        SearchInputComponent,
        HintIconComponent,
        ImageHintIconComponent,
        EnumOutputComponent,
        PriceInputComponent,
        FormErrorsComponent,
        OverlayOutletComponent,
        DropDownContentComponent,
        PageTitleComponent,
        PageTabsComponent,
        PageTabComponent,
        SvgImageComponent,
        RouterOutletLoadingComponent,
        RouterLoadingComponent,
        CountryPickerComponent,
        TabsComponent,
        TabComponent,
        LabelTemplateDirective,
        ContentTemplateDirective,
        SimpleGridComponent,
        PointDetailsComponent,
        CellTemplateDirective,
        PageControlsComponent,
        RowTooltipDirective,
        SvgImageComponent,
        ColumnTemplateDirective,
        ServerTimeComponent,
        ListOptionComponent,
        GridComponent,
        ColumnsComponent,
        Column,
        CellsComponent,
        Cell,
        GridHeaderCellComponent,
        MenuItem,
        MenuButton,
        SwitchComponent,
        HeadingSeparatorComponent,
        InlineSelectorComponent,
        InlineSelectorItemComponent,
        WarningComponent,
        FileInputComponent,
        USDPipe,
        PercentInputComponent,
        AnimatedContentComponent,
        ArrayInputComponent,
        CustomControlDirective,
        DateRangeInputComponent,
        PageTitleItemComponent,
        CopyToClipboardComponent,
        AppTitleComponent,
        KnowledgeBaseLinkComponent,
        SubmitStatusDirective,
        RouterLinkDirective,
        PageTitleContentComponent,
        DropDownModernContentComponent,
        DropDownDirective,
        ResizeDirective,
        SideBarContentComponent,
        HeaderContentComponent,
        SdkStatusComponent,
        IntegrationStatusComponent,
        SkadnetworkStatusComponent,
        HubspotTrackingFormDirective,
        ModalComponent,
        InlineSelectComponent,
        AppStatusesComponent,
        CopyAlertComponent,
        DropDownColorsListComponent,
        FeedbackPopupComponent,
        EmptyStateComponent,
        SafePipe,
        TrackFocusDirective,
        SimpleMultiSelectComponent,
        AppManageSelectComponent,
        PaginationComponent,
        FormGroupComponent,
        FormGroupItemComponent,
        AgGridSearchComponent,
        SavedReportListComponent,
        SavedReportFormComponent,
        HeaderReportFilters,
        RenameReportFormComponent,
        BreadcrumbsComponent,
    ],
    exports: [
        DynamicModule,
        I18NextModule,
        FormsModule,
        ReactiveFormsModule,
        DropDownListComponent,
        ListOptionComponent,
        DropDownMenuComponent,
        MenuItem,
        MenuButton,
        CheckboxComponent,
        TumblerComponent,
        RadioComponent,
        DateEditComponent,
        DateInputComponent,
        TimeInputComponent,
        NumberInputComponent,
        EmailInputComponent,
        PasswordInputComponent,
        InputErrorsComponent,
        InputErrorsTooltipComponent,
        TooltipOutletComponent,
        AccordionComponent,
        DropDownPickerComponent,
        CalendarComponent,
        TooltipDirective,
        ElapsedTextTooltipDirective,
        ServerValidationOnBlurDirective,
        AutoFocusDirective,
        CustomPropertyDirective,
        NumberFormatPipe,
        DurationFormatPipe,
        FormatMetricValuePipe,
        ShortNumberFormatPipe,
        AppUrlPipe,
        DropDownComponent,
        CheckboxTreeComponent,
        SearchInputComponent,
        EnumOutputComponent,
        OverlayOutletComponent,
        DropDownContentComponent,
        HintIconComponent,
        ImageHintIconComponent,
        EnumOutputComponent,
        PriceInputComponent,
        FormErrorsComponent,
        PageTitleComponent,
        PageTabsComponent,
        PageTabComponent,
        SvgImageComponent,
        RouterOutletLoadingComponent,
        RouterLoadingComponent,
        CountryPickerComponent,
        TabsComponent,
        TabComponent,
        LabelTemplateDirective,
        ContentTemplateDirective,
        SimpleGridComponent,
        CellTemplateDirective,
        ColumnTemplateDirective,
        PointDetailsComponent,
        PageControlsComponent,
        RowTooltipDirective,
        SvgImageComponent,
        ServerTimeComponent,
        GridComponent,
        ColumnsComponent,
        Column,
        CellsComponent,
        Cell,
        GridHeaderCellComponent,
        SwitchComponent,
        HeadingSeparatorComponent,
        InlineSelectorComponent,
        InlineSelectorItemComponent,
        WarningComponent,
        FileInputComponent,
        USDPipe,
        PercentInputComponent,
        AnimatedContentComponent,
        ArrayInputComponent,
        CustomControlDirective,
        DateRangeInputComponent,
        PageTitleItemComponent,
        CopyToClipboardComponent,
        AppTitleComponent,
        KnowledgeBaseLinkComponent,
        SubmitStatusDirective,
        RouterLinkDirective,
        PageTitleContentComponent,
        DropDownDirective,
        ResizeDirective,
        SideBarContentComponent,
        HeaderContentComponent,
        SdkStatusComponent,
        IntegrationStatusComponent,
        SkadnetworkStatusComponent,
        HubspotTrackingFormDirective,
        ModalComponent,
        InlineSelectComponent,
        AppStatusesComponent,
        CopyAlertComponent,
        DropDownColorsListComponent,
        FeedbackPopupComponent,
        EmptyStateComponent,
        SafePipe,
        TrackFocusDirective,
        SimpleMultiSelectComponent,
        AppManageSelectComponent,
        PaginationComponent,
        FormGroupComponent,
        FormGroupItemComponent,
        AgGridSearchComponent,
        SavedReportListComponent,
        SavedReportFormComponent,
        HeaderReportFilters,
        RenameReportFormComponent,
        BreadcrumbsComponent,
    ],
    providers: [
        {
            provide: I18NextTitle,
            useClass: I18NextTitleService,
        },
        NumberFormatPipe,
        DurationFormatPipe,
        StatsHelperService,
    ],
})
export class SharedModule {}
